import React, { Component } from "react";
import { Link } from "react-router-dom";
import { SceneArt } from "./illustrations.js";

/* ============================================================
   LEVELUP ONE — Reusable presentational components
   ============================================================ */

/* ---- Section heading (eyebrow + title + subtitle) ---- */
export const SectionHead = ({ eyebrow, title, subtitle, center, className = "" }) => (
  <div className={`${center ? "nx-section-head--center" : ""} ${className}`}>
    {eyebrow && <span className="nx-eyebrow">{eyebrow}</span>}
    <h2 className="nx-heading">{title}</h2>
    {subtitle && <p className="nx-subtitle">{subtitle}</p>}
  </div>
);

/* ---- Button that works as <a>, router <Link>, or <button> ---- */
export const Btn = ({ to, href, variant = "primary", block, children, ...rest }) => {
  const cls = `nx-btn nx-btn--${variant} ${block ? "nx-btn--block" : ""}`;
  if (to) return <Link to={to} className={cls} {...rest}>{children}</Link>;
  if (href) return <a href={href} className={cls} {...rest}>{children}</a>;
  return <button type="button" className={cls} {...rest}>{children}</button>;
};

/* ---- Feature / value card ---- */
export const FeatureCard = ({ icon, title, desc }) => (
  <div className="nx-card">
    <div className="nx-card__icon" aria-hidden="true">{icon}</div>
    <h3 className="nx-card__title">{title}</h3>
    <p className="nx-card__desc">{desc}</p>
  </div>
);

/* ---- Solution / industry row card ---- */
export const SolutionCard = ({ icon, title, desc }) => (
  <div className="nx-solution">
    <div className="nx-solution__icon" aria-hidden="true">{icon}</div>
    <div>
      <h3 className="nx-solution__title">{title}</h3>
      <p className="nx-solution__desc">{desc}</p>
    </div>
  </div>
);

/* ---- AI team chip ---- */
export const TeamChip = ({ face, name, role, bg }) => (
  <div className="nx-team-chip">
    <div className="nx-team-chip__face" style={bg ? { background: bg } : undefined} aria-hidden="true">{face}</div>
    <div className="nx-team-chip__name">{name}</div>
    <div className="nx-team-chip__role">{role}</div>
  </div>
);

/* ---- Journey step ---- */
export const StepCard = ({ num, time, title, desc }) => (
  <div className="nx-step">
    <div className="nx-step__num">{num}</div>
    <span className="nx-step__time">{time}</span>
    <h3 className="nx-step__title">{title}</h3>
    <p className="nx-step__desc">{desc}</p>
  </div>
);

/* ---- Portfolio card (real photo, SVG scene, or emoji cover) ---- */
export const PortfolioCard = ({ icon, cover, scene, photo, tag, title, problem, result }) => (
  <div className="nx-portfolio">
    {photo
      ? <Photo src={photo} alt={title} fallback={icon || "🖼️"} />
      : scene
      ? <SceneArt kind={scene} className="nx-scene" />
      : <div className="nx-portfolio__cover" style={{ background: cover }} aria-hidden="true">{icon}</div>}
    <div className="nx-portfolio__body">
      <span className="nx-portfolio__tag">{tag}</span>
      <h3 className="nx-portfolio__title">{title}</h3>
      <p className="nx-portfolio__row"><b>ปัญหา:</b><span>{problem}</span></p>
      <p className="nx-portfolio__row"><b>ผลลัพธ์:</b><span>{result}</span></p>
    </div>
  </div>
);

/* ---- Accordion FAQ item (self-contained open state) ---- */
export class FaqItem extends Component {
  state = { open: !!this.props.defaultOpen };
  render() {
    const { q, a } = this.props;
    const { open } = this.state;
    return (
      <div className={`nx-faq ${open ? "is-open" : ""}`}>
        <button className="nx-faq__q" onClick={() => this.setState({ open: !open })}>
          <span>{q}</span>
          <span className="nx-faq__icon" aria-hidden="true">+</span>
        </button>
        <div className="nx-faq__a"><div className="nx-faq__a-inner">{a}</div></div>
      </div>
    );
  }
}

/* ---- Solution tile (photo + overlay text, links to detail) ---- */
export const SolutionTile = ({ to, photo, badge, title, tagline, fallback = "🧩" }) => (
  <Link to={to} className="nx-soltile">
    <Photo src={photo} alt={title} fallback={fallback} />
    <div className="nx-soltile__body">
      {badge && <span className="nx-soltile__badge">{badge}</span>}
      <h3 className="nx-soltile__title">{title}</h3>
      {tagline && <span className="nx-soltile__tag">{tagline}</span>}
      <span className="nx-soltile__link">ดูเพิ่มเติม →</span>
    </div>
  </Link>
);

/* ---- Testimonial / review card ---- */
export const Quote = ({ text, name, role, avatar }) => (
  <div className="nx-quote">
    <div className="nx-quote__stars">★★★★★</div>
    <p className="nx-quote__text">“{text}”</p>
    <div className="nx-quote__who">
      <span className="nx-quote__avatar" aria-hidden="true">{avatar}</span>
      <div>
        <div className="nx-quote__name">{name}</div>
        <div className="nx-quote__role">{role}</div>
      </div>
    </div>
  </div>
);

/* ---- Inner page hero with breadcrumb ---- */
export const PageHero = ({ eyebrow, title, subtitle, crumb }) => (
  <header className="nx-pagehero">
    <div className="container">
      {crumb && (
        <div className="nx-breadcrumb">
          <Link to="/">หน้าแรก</Link> &nbsp;/&nbsp; <span>{crumb}</span>
        </div>
      )}
      {eyebrow && <span className="nx-eyebrow">{eyebrow}</span>}
      <h1 className="nx-pagehero__title nx-ink mt-3">{title}</h1>
      {subtitle && <p className="nx-subtitle mt-2">{subtitle}</p>}
    </div>
  </header>
);

/* ---- CTA band ---- */
export const CtaBand = ({ title, text }) => (
  <section className="nx-section">
    <div className="container">
      <div className="nx-cta">
        <h2>{title || "พร้อมให้ AI เริ่มทำงานบนระบบเดิมของคุณแล้วหรือยัง?"}</h2>
        <p>{text || "เริ่มต้นด้วยคำปรึกษาฟรี 30 นาที ไม่มีข้อผูกมัด ทีมงานของเราพร้อมช่วยออกแบบโซลูชันให้ธุรกิจคุณ"}</p>
        <div className="nx-cta__actions">
          <Link to="/booking" className="nx-btn nx-btn--light">จองคำปรึกษาฟรี</Link>
          <a href="https://line.me" className="nx-btn nx-btn--accent">คุย LINE ก่อน</a>
        </div>
      </div>
    </div>
  </section>
);

/* ---- Animated count-up number (triggers on scroll into view) ---- */
export class CountUp extends Component {
  state = { val: 0 };
  ref = React.createRef();
  componentDidMount() {
    this.obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { this.run(); this.obs.unobserve(e.target); }
      }),
      { threshold: 0.4 }
    );
    if (this.ref.current) this.obs.observe(this.ref.current);
  }
  componentWillUnmount() {
    if (this.obs) this.obs.disconnect();
    if (this.raf) cancelAnimationFrame(this.raf);
  }
  run() {
    const { end, decimals = 0, duration = 1500 } = this.props;
    const target = Math.round(end * Math.pow(10, decimals));
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      this.setState({ val: Math.round(target * eased) });
      if (p < 1) this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }
  render() {
    const { prefix = "", suffix = "", decimals = 0 } = this.props;
    const shown = (this.state.val / Math.pow(10, decimals)).toFixed(decimals);
    return <span ref={this.ref}>{prefix}{shown}{suffix}</span>;
  }
}

/* ---- Floating back-to-top button (global) ---- */
export class BackToTop extends Component {
  state = { show: false };
  componentDidMount() {
    this.onScroll = () => this.setState({ show: window.scrollY > 500 });
    window.addEventListener("scroll", this.onScroll, { passive: true });
  }
  componentWillUnmount() { window.removeEventListener("scroll", this.onScroll); }
  render() {
    return (
      <button
        className={`nx-backtop ${this.state.show ? "is-show" : ""}`}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="กลับขึ้นด้านบน"
      >↑</button>
    );
  }
}

/* ---- Real photo with graceful fallback (Unsplash etc.) ---- */
export class Photo extends Component {
  state = { err: false };
  render() {
    const { src, alt = "", className = "", fallback = "🖼️" } = this.props;
    if (this.state.err || !src) {
      return <div className={`nx-photo nx-photo--fallback ${className}`}><span aria-hidden="true">{fallback}</span></div>;
    }
    return (
      <div className={`nx-photo ${className}`}>
        <img src={src} alt={alt} loading="lazy" onError={() => this.setState({ err: true })} />
        <span className="nx-photo__overlay" />
      </div>
    );
  }
}

/* ---- Scroll reveal wrapper (IntersectionObserver) ---- */
export class Reveal extends Component {
  ref = React.createRef();
  componentDidMount() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            this.observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    if (this.ref.current) this.observer.observe(this.ref.current);
  }
  componentWillUnmount() {
    if (this.observer) this.observer.disconnect();
  }
  render() {
    const { children, delay, className = "" } = this.props;
    return (
      <div
        ref={this.ref}
        className={`nx-reveal ${className}`}
        style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      >
        {children}
      </div>
    );
  }
}
